import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Drawer
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ViewListIcon from "@mui/icons-material/ViewList";
import TimelineIcon from "@mui/icons-material/Timeline";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BuildIcon from "@mui/icons-material/Build";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
  collection,
  query,
  where,
  addDoc,
  updateDoc,
  doc,
  setDoc,
  getDoc,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebase";
import { tryResolveTenantId } from "../utils/tenant";
import AddShiftDialog from "./AddShiftDialog";
import ScheduleTimelineView from "./ScheduleTimelineView";
import ScheduleGrid from "./ScheduleGridWithDnD";
import AnalyticsTable from "./AnalyticsTable";
import StaffingTargetsPanel from "./StaffingTargetsPanel";
import ClearScheduleDialog from "./ClearScheduleDialog";
import CopyScheduleDialog from "./CopyScheduleDialog";
import { getMonday, addDays, toMinutes } from "../utils";
import {
  useEmployees,
  useShifts,
  useRoles,
  useLocations,
  useSalesProjections,
  useForecastedSales,
  useActualSales,
} from "../hooks/useFirestoreData";
import { useDepartments } from "../hooks/useDepartments";
import { useSchedulePublication } from "../hooks/useSchedulePublication";
import { getBusinessDateKey } from "../utils/businessDateKey";
import { computeShiftTimestamps, resolveLocationTimezone } from "../utils/shiftTimezone";

// Helpers for parsing and formatting
// function toMinutes(t) {
//   if (!t) return 0;
//   const [time, ampm] = t.split(" ");
//   let [h, m] = time.split(":").map(Number);
//   if (ampm === "PM" && h < 12) h += 12;
//   if (ampm === "AM" && h === 12) h = 0;
//   return h * 60 + m;
// }
// function formatTime(t) {
//   if (!t) return "";
//   const [hourStr, minute] = t.split(":");
//   let h = parseInt(hourStr, 10);
//   const ampm = h >= 12 ? "PM" : "AM";
//   h = h % 12 || 12;
//   return `${h}:${minute} ${ampm}`;
// }

// Utilities for date math
// function getMonday(date) {
//   const d = new Date(date);
//   d.setHours(0, 0, 0, 0);
//   const day = d.getDay();
//   const diff = (day + 6) % 7;
//   d.setDate(d.getDate() - diff);
//   return d;
// }
// function addDays(date, days) {
//   const d = new Date(date);
//   d.setDate(d.getDate() + days);
//   return d;
// }

export default function ScheduleCreation() {
  const SHIFT_LOCKED_MESSAGE = "This shift has recorded punches and can’t be moved. To correct worked hours, edit the time entry.";
  // Week start state
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    }),
    [weekStart]
  );

  // Firestore hooks
  const employees = useEmployees();
  const [selectedLocation, setSelectedLocation] = useState("");
  const locations = useLocations(selectedLocation, setSelectedLocation);
  
  // Get timezone for selected location
  const selectedLocationObj = locations.find(loc => loc.id === selectedLocation);
  const locationTimeZone = selectedLocationObj?.timeZone || 
    selectedLocationObj?.timezone || 'America/Chicago'; // fallback
  
  const shifts = useShifts(weekStart, selectedLocation, locationTimeZone);
  const roles = useRoles();
  const rolesForSelectedLocation = useMemo(() => {
    if (!selectedLocation) return [];
    return (roles || []).filter((r) => r?.locationId === selectedLocation);
  }, [roles, selectedLocation]);
  const [salesProjections, salesProjectionsInput, setSalesProjections, setSalesProjectionsInput] = useSalesProjections(weekDays);
  const [forecastedSales, forecastedSalesInput, setForecastedSales, setForecastedSalesInput] = useForecastedSales(weekDays);
  const [actualSales, actualSalesInput, setActualSales, setActualSalesInput] = useActualSales(weekDays);
  const departments = useDepartments();
  const [selectedDepartment, setSelectedDepartment] = useState("");
  
  // Schedule publication management
  const { 
    publicationStatus, 
    loading: publicationLoading,
    publishSchedule, 
    unpublishSchedule,
    isPublished 
  } = useSchedulePublication(weekStart);

  const [dialog, setDialog] = useState({ open: false, emp: null, date: null, shift: null, dept: null });
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent duplicate submissions
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuContext, setMenuContext] = useState({ emp: null, date: null, shift: null, dept: null, role: null });
  const [menuPosition, setMenuPosition] = useState({ mouseX: null, mouseY: null });
  const [copyBuffer, setCopyBuffer] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [staffingTargetsOpen, setStaffingTargetsOpen] = useState(false);

  // For clear/copy dialogs
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyChoice, setCopyChoice] = useState("last");
  const [customCopyDate, setCustomCopyDate] = useState(getMonday(new Date()));
  // Tools menu state
  const [toolsMenuAnchor, setToolsMenuAnchor] = useState(null);
  const openToolsMenu = (e) => setToolsMenuAnchor(e.currentTarget);
  const closeToolsMenu = () => setToolsMenuAnchor(null);
  const handleToolsClearSchedule = () => { closeToolsMenu(); setClearDialogOpen(true); };
  const handleToolsStaffingTargets = () => { closeToolsMenu(); setStaffingTargetsOpen(true); };

  // View mode state
  const [viewMode, setViewMode] = useState("classic"); // "classic" or "smart"
  // Header offset (pixels) so child grid can position sticky elements correctly
  const headerRef = React.useRef(null);
  const [headerOffset, setHeaderOffset] = useState(0);
  // Remember the baseline fixed/sticky top elements present on mount so transient popovers/menus
  // opened later don't get counted as part of the header height.
  const baselineFixedTopElsRef = React.useRef(null);

  const measureFixedTopExtra = React.useCallback(() => {
    let fixedTopExtra = 0;
    try {
      if (!baselineFixedTopElsRef.current) {
        const base = [];
        const all = Array.from(document.querySelectorAll('body *'));
        for (const el of all) {
          if (!el || !(el.getBoundingClientRect instanceof Function)) continue;
          if (headerRef.current && el.contains && el.contains(headerRef.current)) continue;
          const style = window.getComputedStyle(el);
          if (style.position === 'fixed' || style.position === 'sticky') {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 1 && rect.bottom > 0 && rect.width > 0 && rect.height > 0) {
              base.push(el);
            }
          }
        }
        baselineFixedTopElsRef.current = base;
      }
      const baseEls = baselineFixedTopElsRef.current || [];
      for (const el of baseEls) {
        try {
          const rect = el.getBoundingClientRect();
          if (rect && rect.height > 0) {
            fixedTopExtra = Math.max(fixedTopExtra, Math.round(rect.height));
          }
        } catch (e) {}
      }
    } catch (err) {}
    return fixedTopExtra;
  }, []);

  // Measure header height and update on resize
  useEffect(() => {
    function measure() {
      // Measure header height but guard against bogus values (e.g. very large numbers during layout)
      const raw = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
      let h = Math.round(raw || 0);
      // Clamp to a sensible range: 0..window.innerHeight. If measurement is invalid, fall back to 0.
      if (!Number.isFinite(h) || h < 0 || h > (window?.innerHeight || 10000)) {
        h = 0;
      }
      const combined = h + measureFixedTopExtra();
      setHeaderOffset(prev => (prev === combined ? prev : combined));
    }
    // Measure after first paint to allow layout to settle
    const timeouts = [];
    timeouts.push(setTimeout(measure, 0));

    // Also schedule a couple of delayed re-measures because fonts, images, or other async
    // layout changes can happen after first paint (opening devtools triggers a resize which
    // previously corrected the measurement). These delayed attempts help stabilize the value.
    timeouts.push(setTimeout(measure, 250));
    timeouts.push(setTimeout(measure, 1000));

    // Re-measure on window resize
    window.addEventListener('resize', measure);

    // Use ResizeObserver to catch changes to the header element itself
    let ro;
    try {
      if (window.ResizeObserver && headerRef.current) {
        ro = new ResizeObserver(measure);
        ro.observe(headerRef.current);
      }
    } catch (err) {
      // ignore
    }

    return () => {
      window.removeEventListener('resize', measure);
      if (ro && ro.disconnect) ro.disconnect();
      timeouts.forEach(t => clearTimeout(t));
    };
  }, [measureFixedTopExtra]);

  // Handle menu closing on outside clicks and escape key
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Only close menu if clicking outside the menu and not on a context menu trigger
      // If we've just opened the menu, ignore this first global click (it likely originated from the opener)
      if (ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false;
        return;
      }
      if ((menuAnchor || menuPosition.mouseX !== null) && !e.target.closest('[role="menu"]') && !e.defaultPrevented) {
        closeMenu();
      }
    };

    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && (menuAnchor || menuPosition.mouseX !== null)) {
        closeMenu();
      }
    };

    if (menuAnchor || menuPosition.mouseX !== null) {
      // Add event listeners when menu is open
      document.addEventListener('click', handleGlobalClick, true);
      document.addEventListener('keydown', handleEscapeKey, true);
    }

    return () => {
      // Cleanup event listeners
      document.removeEventListener('click', handleGlobalClick, true);
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [menuAnchor, menuPosition]);

  // Ref to ignore the very next global click after opening the menu. This prevents the opener click
  // from immediately closing the menu when opening via programmatic coordinates or synthetic events.
  const ignoreNextClickRef = React.useRef(false);

  // Menu functions
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuPosition({ mouseX: null, mouseY: null });
  };
  const openMenu = (e, emp, date, shift, dept, role = null) => { 
    e.preventDefault(); 
    e.stopPropagation(); // Prevent event bubbling
    
    // Use mouse position for better context menu positioning
  ignoreNextClickRef.current = true;
    setMenuPosition({
      mouseX: e.clientX - 2,
      mouseY: e.clientY - 4,
    });
    setMenuAnchor(e.currentTarget); // Keep this as fallback
    setMenuContext({ emp, date, shift, dept, role }); 
  };

  // Filter employees by selected location
  const filteredEmployees = useMemo(() => {
    if (!selectedLocation) return employees;
    return employees.filter(e => {
      // Show if locationId matches
      if (e.locationId === selectedLocation) return true;
      // Show if assignments for this location exist and are not empty
      if (e.assignments && e.assignments[selectedLocation]) {
        // If assignments[selectedLocation] has at least one department with roles
        return Object.values(e.assignments[selectedLocation]).some(
          rolesArr => Array.isArray(rolesArr) ? rolesArr.length > 0 : Object.keys(rolesArr || {}).length > 0
        );
      }
      return false;
    });
  }, [employees, selectedLocation]);

  const filteredShifts = useMemo(() => {
    if (!selectedLocation) return shifts;
    const filtered = shifts.filter(s => s.locationId === selectedLocation);
    // Filter shifts for selected location and current week
    return filtered;
  }, [shifts, selectedLocation]);

  // Determine if there are pending changes to a published schedule
  const hasPendingChanges = useMemo(() => {
    if (!isPublished) return false;
    return filteredShifts.some(s => 
      s?.pendingDelete === true ||
      (s?.pendingUpdate && Object.keys(s.pendingUpdate || {}).length > 0) ||
      (s?.isPublished !== true && (s?.pendingCreate === true))
    );
  }, [filteredShifts, isPublished]);

  // Count of items affected when publishing changes (create/update/delete)
  const pendingChangesCount = useMemo(() => {
    return (filteredShifts || []).reduce((cnt, s) => {
      const hasUpdate = s?.pendingUpdate && Object.keys(s.pendingUpdate || {}).length > 0;
      const hasCreate = s?.pendingCreate === true || (s?.isPublished === false && isPublished);
      const hasDelete = s?.pendingDelete === true;
      return cnt + (hasUpdate || hasCreate || hasDelete ? 1 : 0);
    }, 0);
  }, [filteredShifts, isPublished]);

  // Count of shifts that will be published initially (non-deleted shifts in current week/location)
  const publishCount = useMemo(() => {
    return (filteredShifts || []).filter(s => !s.deleted).length;
  }, [filteredShifts]);

  // Build deptNameMap: supports both deptId and deptId_roleId keys
  const deptNameMap = useMemo(() => {
    const map = {};
    departments.forEach(d => { map[d.id] = d.name; });
    
    // For compound keys (deptId_roleId), return "Department - Role"
    const getDeptRoleName = (key) => {
      if (key.includes('_')) {
        const [deptId, roleId] = key.split('_');
        const deptName = map[deptId] || deptId;
        const role = roles.find(r => r.id === roleId);
        const roleName = role ? role.name : roleId;
        return `${deptName} - ${roleName}`;
      }
      return map[key] || key;
    };
    
    return new Proxy(map, {
      get: (target, prop) => {
        if (typeof prop === 'string') {
          return getDeptRoleName(prop);
        }
        return target[prop];
      }
    });
  }, [departments, roles]);

  // Navigation
  const prevWeek = () => setWeekStart(d => addDays(d, -7));
  const nextWeek = () => setWeekStart(d => addDays(d, 7));
  const goToday = () => setWeekStart(getMonday(new Date()));

  // Departments for current location
  const departmentsForLocation = useMemo(() => {
    return departments.filter(d => d.locationId === selectedLocation);
  }, [departments, selectedLocation]);

  // Ensure selectedDepartment stays valid for location
  useEffect(() => {
    if (!selectedLocation) { setSelectedDepartment(""); return; }
    if (selectedDepartment && !departmentsForLocation.some(d => d.id === selectedDepartment)) {
      setSelectedDepartment("");
    }
  }, [selectedLocation, departmentsForLocation, selectedDepartment]);

  // Group employees by department and role for the selected location
  const byDept = useMemo(() => {
    const map = {};
    (filteredEmployees || []).forEach(e => {
      const locAssign = e.assignments && e.assignments[selectedLocation];

      if (locAssign && locAssign.departments) {
        let allAssignedRoles = {};
        let primaryRole = null;

        Object.entries(locAssign.departments).forEach(([deptId, deptObj]) => {
          if (!deptObj) return;
          const rolesData = deptObj.roles ?? {};

          if (Array.isArray(rolesData)) {
            // Legacy format: array of role IDs
            rolesData.forEach(roleId => {
              allAssignedRoles[`${deptId}_${roleId}`] = true;
            });
          } else {
            // New format: { roleId: { assigned: bool, isPrimary: bool } }
            Object.entries(rolesData).forEach(([roleId, meta]) => {
              if (!meta) return;
              if (meta.assigned || meta.isPrimary) {
                allAssignedRoles[`${deptId}_${roleId}`] = true;
                if (meta.isPrimary) primaryRole = { deptId, roleId };
              }
            });
          }
        });

        if (primaryRole) {
          const key = `${primaryRole.deptId}_${primaryRole.roleId}`;
          map[key] = map[key] || [];
          map[key].push({ ...e, allAssignedRoles });
        } else if (Object.keys(allAssignedRoles).length > 0) {
          Object.keys(allAssignedRoles).forEach(key => {
            map[key] = map[key] || [];
            map[key].push({ ...e, allAssignedRoles });
          });
        } else {
          const unassignedKey = "Unassigned_unassigned";
          map[unassignedKey] = map[unassignedKey] || [];
          map[unassignedKey].push({ ...e, allAssignedRoles: null });
        }
      } else {
        // No assignments for this location
        const unassignedKey = "Unassigned_unassigned";
        map[unassignedKey] = map[unassignedKey] || [];
        map[unassignedKey].push({ ...e, allAssignedRoles: null });
      }
    });
    return map;
  }, [filteredEmployees, selectedLocation]);

  // Build a departmental hierarchy view: { [deptId]: { [roleId]: Employee[] } }
  const byDeptHierarchy = useMemo(() => {
    const hierarchy = {};
    Object.entries(byDept).forEach(([deptRoleKey, employees]) => {
      if (deptRoleKey === "Unassigned_unassigned") {
        hierarchy["Unassigned"] = hierarchy["Unassigned"] || {};
        hierarchy["Unassigned"]["unassigned"] = employees;
      } else {
        const [deptId, roleId] = deptRoleKey.split('_');
        hierarchy[deptId] = hierarchy[deptId] || {};
        hierarchy[deptId][roleId] = employees;
      }
    });
    return hierarchy;
  }, [byDept]);

  // Filter by selected department for the view
  const byDeptHierarchyView = useMemo(() => {
    if (!selectedDepartment) return byDeptHierarchy;
    return byDeptHierarchy && byDeptHierarchy[selectedDepartment]
      ? { [selectedDepartment]: byDeptHierarchy[selectedDepartment] }
      : {};
  }, [byDeptHierarchy, selectedDepartment]);

  const viewShifts = useMemo(() => {
    if (!selectedDepartment) return filteredShifts;
    return filteredShifts.filter(s => s.dept === selectedDepartment);
  }, [filteredShifts, selectedDepartment]);

  const empIdSet = useMemo(() => {
    return new Set(filteredEmployees.map(e => e.id));
  }, [filteredEmployees]);

  // Compute analytics using assigned role for each shift and human-readable role names
  const analytics = useMemo(() => {
    const totalShifts = {}, totalHours = {}, laborCost = {}, splh = {}, laborPct = {}, deptPct = {}, hoursByDept = {}, costByDept = {};
    Object.entries(byDept).forEach(([dept, list]) => {
      hoursByDept[dept] = {};
      costByDept[dept] = {};
      deptPct[dept] = {};
      list.forEach(e => (hoursByDept[dept][e.id] = 0));
    });

    // Calculate analytics using business day logic for overnight shift support
    const calculateAnalyticsForDay = async (d) => {
      const key = d.toISOString().split("T")[0];
      let ds = 0, dh = 0, dc = 0;
      
      Object.entries(byDept).forEach(([dept, list]) => {
        costByDept[dept][key] = 0;
        list.forEach(emp => {
          shifts.filter(s => {
            if (s.deleted || s.employeeId !== emp.id) return false;
            
            // For overnight shifts, use business day logic to determine which day they belong to
            const shiftDate = s.shiftDate.toDate ? s.shiftDate.toDate() : new Date(s.shiftDate);
            
            // Use simple date comparison for now, but could use business day logic for overnight shifts
            return shiftDate.toISOString().split("T")[0] === key;
          }).forEach(s => {
            const startMinutes = toMinutes(s.shiftStartTime);
            const endMinutes = toMinutes(s.shiftEndTime);
            
            // Handle overnight shifts - if end time is before start time, it's next day
            let hrs;
            if (endMinutes < startMinutes) {
              // Overnight shift
              hrs = ((24 * 60) - startMinutes + endMinutes) / 60;
            } else {
              // Regular shift
              hrs = (endMinutes - startMinutes) / 60;
            }
            
            // Use assigned role for rate calculation
            let rate = emp.defaultRate || 15.00; // Default fallback rate
            if (emp.rates && s.role && emp.rates[s.role] !== undefined) {
              rate = emp.rates[s.role];
            }
            ds++; dh += hrs; dc += rate * hrs;
            hoursByDept[dept][emp.id] += hrs;
            costByDept[dept][key] += rate * hrs;
          });
        });
      });
      
      const sale =
        typeof forecastedSales[key] === "number"
          ? forecastedSales[key]
          : salesProjections[key] || 0;
      totalShifts[key] = ds;
      totalHours[key] = dh;
      laborCost[key] = dc;
      splh[key] = dh > 0 ? sale / dh : 0;
      laborPct[key] = sale > 0 ? (dc / sale) * 100 : 0;
      Object.keys(costByDept).forEach(dept => {
        deptPct[dept][key] = sale > 0 ? (costByDept[dept][key] / sale) * 100 : 0;
      });
    };

    // Process each day
    weekDays.forEach(d => {
      calculateAnalyticsForDay(d);
    });

    // Calculate week totals only for the current week days
    const weekForecastedSales = weekDays.reduce((total, d) => {
      const key = d.toISOString().split("T")[0];
      const dayForecast =
        typeof forecastedSales[key] === "number"
          ? forecastedSales[key]
          : salesProjections[key] || 0;
      return total + dayForecast;
    }, 0);
    const weekActualSales = weekDays.reduce((total, d) => {
      const key = d.toISOString().split("T")[0];
      return total + (actualSales[key] || 0);
    }, 0);
    const weekH = Object.values(totalHours).reduce((a, v) => a + (v || 0), 0);
    const weekC = Object.values(laborCost).reduce((a, v) => a + (v || 0), 0);
    const weekP = weekForecastedSales > 0 ? (weekC / weekForecastedSales) * 100 : 0;

    return {
      totalShifts,
      totalHours,
      laborCost,
      splh,
      laborPct,
      deptPct,
      hoursByDept,
      weekTotals: {
        sales: weekForecastedSales,
        forecastedSales: weekForecastedSales,
        actualSales: weekActualSales,
        actualSalesByDay: actualSales,
        hours: weekH,
        cost: weekC,
        pct: weekP,
      },
    };
  }, [shifts, byDept, salesProjections, forecastedSales, actualSales, weekDays]);

  // ---- CREATE OPEN SHIFT ----
  const handleCreateOpenShift = () => {
    const { date, dept } = menuContext;
    // Open dialog for creating an open shift (no employee assigned)
    setDialog({ 
      open: true, 
      emp: null, // No employee for open shifts
      date, 
      shift: null, 
      dept,
      isOpenShift: true // Flag to indicate this is an open shift
    });
    closeMenu();
  };

  // Menu/Dialog/Clipboard
  const openDialog = () => setDialog({ ...menuContext, open: true });
  // Open the Add/Edit Shift dialog immediately with explicit context (bypass menu)
  const openDialogImmediate = (e, emp, date, shift, dept, role = null, isOpenShift = false) => {
    if (e && typeof e.stopPropagation === 'function') {
      try { e.stopPropagation(); e.preventDefault(); } catch (err) { /* ignore */ }
    }
    setDialog({ emp, date, shift, dept, role, open: true, isOpenShift });
    closeMenu();
  };

  // Open the menu immediately using explicit coordinates (avoids race with global click handlers)
  const openMenuImmediate = (emp, date, shift, dept, role = null, x = null, y = null, source = null) => {
    // If coordinates not provided, center on window
    const mouseX = x != null ? x : window.innerWidth / 2;
    const mouseY = y != null ? y : window.innerHeight / 2;
  ignoreNextClickRef.current = true;
  setMenuPosition({ mouseX, mouseY });
    setMenuAnchor(null);
  setMenuContext({ emp, date, shift, dept, role, source });
  };
  const closeDialog = () => {
    setDialog({ open: false, emp: null, date: null, shift: null, dept: null, role: null, isOpenShift: false });
    setIsSubmitting(false); // Reset submission flag
  };

  const toJsDate = (value) => {
    if (!value) return null;
    if (typeof value.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const hasRecordedPunches = (timecard) => {
    if (!timecard || typeof timecard !== "object") return false;
    const ids = Array.isArray(timecard.timeEntryIds) ? timecard.timeEntryIds : [];
    if (ids.length > 0) return true;
    if (timecard.firstClockInAt || timecard.lastClockOutAt) return true;
    if (Number(timecard.clockInCount || 0) > 0) return true;
    if (Number(timecard.clockOutCount || 0) > 0) return true;
    return false;
  };

  const getEffectiveShift = (shift) => {
    if (!shift) return shift;
    const hasPendingUpdate = !!(isPublished && shift.pendingUpdate && Object.keys(shift.pendingUpdate || {}).length > 0);
    return hasPendingUpdate ? { ...shift, ...shift.pendingUpdate } : shift;
  };

  const isPastShift = (shift) => {
    const effectiveShift = getEffectiveShift(shift);
    if (!effectiveShift) return false;

    let endAt = toJsDate(effectiveShift.endAt);
    if (!endAt && effectiveShift.localDateKey && effectiveShift.shiftStartTime && effectiveShift.shiftEndTime) {
      const shiftZone = resolveLocationTimezone(
        locations.find((loc) => loc.id === effectiveShift.locationId) ||
        locations.find((loc) => loc.id === selectedLocation) ||
        { timeZone: effectiveShift.timeZone || "America/Chicago" }
      );
      const tzData = computeShiftTimestamps(
        effectiveShift.localDateKey,
        effectiveShift.shiftStartTime,
        effectiveShift.shiftEndTime,
        shiftZone
      );
      endAt = toJsDate(tzData?.endAt);
    }

    if (!endAt) {
      const shiftDate = toJsDate(effectiveShift.shiftDate);
      if (!shiftDate) return false;
      const shiftZone = resolveLocationTimezone(
        locations.find((loc) => loc.id === effectiveShift.locationId) ||
        locations.find((loc) => loc.id === selectedLocation) ||
        { timeZone: effectiveShift.timeZone || "America/Chicago" }
      );
      const shiftKey = DateTime.fromJSDate(shiftDate, { zone: shiftZone }).toISODate();
      const todayKey = DateTime.now().setZone(shiftZone).toISODate();
      return !!(shiftKey && todayKey && shiftKey < todayKey);
    }

    return endAt.getTime() < Date.now();
  };

  const isShiftLockedForScheduleEdits = async (tenantId, shift) => {
    if (!tenantId || !shift?.id) return false;
    if (!isPastShift(shift)) return false;
    const timecardRef = doc(db, `tenants/${tenantId}/timecards`, `shift_${shift.id}`);
    const timecardSnap = await getDoc(timecardRef);
    if (!timecardSnap.exists()) return false;
    return hasRecordedPunches(timecardSnap.data() || {});
  };

  // Publication handlers
  const handlePublishSchedule = async () => {
    try {
      // Get the logged-in user's name from localStorage
      let publishedBy = 'Manager'; // Fallback
      try {
        const adminUser = localStorage.getItem('adminUser');
        if (adminUser) {
          const user = JSON.parse(adminUser);
          publishedBy = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Manager';
        }
      } catch (e) {
        console.warn('Could not retrieve user name for publication:', e);
      }
      
      await publishSchedule(publishedBy, hasPendingChanges ? 'Publishing staged changes' : 'Schedule published from Schedule Creation');
      setSnackbar({
        open: true,
        message: hasPendingChanges ? 'Changes published successfully!' : 'Schedule published successfully! Employees can now see their shifts.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error publishing schedule:', error);
      setSnackbar({
        open: true,
        message: 'Error publishing schedule: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleUnpublishSchedule = async () => {
    try {
      // Get the logged-in user's name from localStorage
      let unpublishedBy = 'Manager'; // Fallback
      try {
        const adminUser = localStorage.getItem('adminUser');
        if (adminUser) {
          const user = JSON.parse(adminUser);
          unpublishedBy = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Manager';
        }
      } catch (e) {
        console.warn('Could not retrieve user name for unpublication:', e);
      }
      
      await unpublishSchedule(unpublishedBy, 'Schedule unpublished for revisions');
      setSnackbar({
        open: true,
        message: 'Schedule unpublished. Employees will no longer see these shifts.',
        severity: 'warning'
      });
    } catch (error) {
      console.error('Error unpublishing schedule:', error);
      setSnackbar({
        open: true,
        message: 'Error unpublishing schedule: ' + error.message,
        severity: 'error'
      });
    }
  };
  
  const handleDialogSave = async ({ shiftStartTime, shiftEndTime, role, notes, breaks }) => {
    const { emp, date, shift, dept, isOpenShift } = dialog;
    if (isSubmitting || !dialog.open) return;
    setIsSubmitting(true);

    // Resolve tenantId for tenant-scoped shifts collection
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot save shift: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      setIsSubmitting(false);
      return;
    }

    const shiftDate = date instanceof Date ? date : new Date(date);

    // Get location and compute timezone-safe timestamps
    const location = locations.find(loc => loc.id === selectedLocation);
    const timeZone = resolveLocationTimezone(location);
    
    // Format date as YYYY-MM-DD in location timezone
    const localDateStr = DateTime.fromJSDate(shiftDate, { zone: timeZone }).toISODate();
    
    // Compute UTC timestamps and keys
    const tzData = computeShiftTimestamps(
      localDateStr,
      shiftStartTime,
      shiftEndTime,
      timeZone
    );
    const businessDateKey = getBusinessDateKey(location, tzData.startAt.toDate());
    const localWeekKey = DateTime.fromISO(businessDateKey, { zone: timeZone }).toFormat("kkkk-'W'WW");

    // Get role name for denormalization
    const roleObj = roles.find(r => r.id === role);
    const roleName = roleObj?.name || roleObj?.title || "";
    const rolePayrollCode = roleObj?.rolePayrollCode || null;

    const payload = {
      dept,
      role,
      roleName, // Add role name for easier querying
      rolePayrollCode,
      shiftDate: tzData.startAt,
      shiftStartTime,
      shiftEndTime,
      notes: notes || "",
      breaks: breaks || [],
      deleted: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      locationId: selectedLocation,
      isOpenShift: isOpenShift || false,
      // Timezone-safe fields
      startAt: tzData.startAt,
      endAt: tzData.endAt,
      timeZone: timeZone,
      localDateKey: businessDateKey,
      localWeekKey,
      shiftDateKey: businessDateKey,
    };

    const shouldBeOpenShift = !emp || isOpenShift;
    if (emp && !shouldBeOpenShift) {
      payload.employeeId = emp.id;
      payload.employeeName = emp.name || (emp ? [emp.firstName, emp.lastName].filter(Boolean).join(" ") : null);
      payload.isOpenShift = false;
    } else {
      payload.employeeId = null;
      payload.employeeName = null;
      payload.isOpenShift = true;
    }

    try {
      if (shift?.id) {
        if (await isShiftLockedForScheduleEdits(tenantId, shift)) {
          setSnackbar({ open: true, message: SHIFT_LOCKED_MESSAGE, severity: "warning" });
          closeDialog();
          return;
        }
        // Editing existing shift
        if (isPublished) {
          // Stage update instead of applying immediately
          const stagedUpdate = {
            dept: payload.dept,
            role: payload.role,
            roleName: payload.roleName,
            rolePayrollCode: payload.rolePayrollCode,
            shiftDate: payload.shiftDate,
            shiftStartTime: payload.shiftStartTime,
            shiftEndTime: payload.shiftEndTime,
            notes: payload.notes,
            breaks: payload.breaks,
            isOpenShift: payload.isOpenShift,
            employeeId: payload.employeeId ?? null,
            employeeName: payload.employeeName ?? null,
            locationId: selectedLocation,
            // Include timezone fields in staged updates
            startAt: payload.startAt,
            endAt: payload.endAt,
            timeZone: payload.timeZone,
            localDateKey: payload.localDateKey,
            localWeekKey: payload.localWeekKey,
          };
          await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), {
            pendingUpdate: stagedUpdate,
            updatedAt: new Date(),
          });
        } else {
          await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), payload);
        }
      } else {
        // Creating new shift
        if (isPublished) {
          await addDoc(collection(db, `tenants/${tenantId}/shifts`), {
            ...payload,
            isPublished: false,
            pendingCreate: true,
          });
        } else {
          await addDoc(collection(db, `tenants/${tenantId}/shifts`), payload);
        }
      }

      const message = isPublished
        ? (shift?.id ? "Change staged. Publish to make it live." : "Shift added as draft. Publish to make it visible to employees.")
        : (shouldBeOpenShift ? "Open shift saved successfully." : "Shift saved successfully.");
      setSnackbar({ open: true, message, severity: "success" });
      closeDialog();
    } catch (err) {
      console.error("Error saving shift:", err);
      setSnackbar({ open: true, message: `Error saving shift: ${err.message || err}`, severity: "error" });
      closeDialog();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogDelete = async (shift) => {
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot delete shift: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      return;
    }
    try {
      if (shift?.id) {
        if (await isShiftLockedForScheduleEdits(tenantId, shift)) {
          setSnackbar({ open: true, message: SHIFT_LOCKED_MESSAGE, severity: "warning" });
          closeDialog();
          return;
        }
        if (isPublished) {
          // If the shift itself is a draft (unpublished/new), remove it immediately.
          const isDraftShift = (shift.isPublished !== true) || (shift.pendingCreate === true);
          if (isDraftShift) {
            await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { deleted: true, updatedAt: new Date() });
            setSnackbar({ open: true, message: "Draft shift removed.", severity: "success" });
          } else {
            // For already published shifts, stage deletion to be applied on publish
            await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { pendingDelete: true, updatedAt: new Date() });
            setSnackbar({ open: true, message: "Shift marked for deletion. Publish to confirm.", severity: "info" });
          }
        } else {
          // Week not published: delete immediately
          await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { deleted: true, updatedAt: new Date() });
          setSnackbar({ open: true, message: "Shift deleted.", severity: "success" });
        }
      }
      closeDialog();
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting shift: ${err.message || err}`, severity: "error" });
    }
  };

  const handleDelete = async () => {
    const { shift } = menuContext;
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot delete shift: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      closeMenu();
      return;
    }
    try {
      if (shift?.id) {
        if (await isShiftLockedForScheduleEdits(tenantId, shift)) {
          setSnackbar({ open: true, message: SHIFT_LOCKED_MESSAGE, severity: "warning" });
          closeMenu();
          return;
        }
        if (isPublished) {
          // If the shift itself is a draft (unpublished/new), remove it immediately.
          const isDraftShift = (shift.isPublished !== true) || (shift.pendingCreate === true);
          if (isDraftShift) {
            await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { deleted: true, updatedAt: new Date() });
            setSnackbar({ open: true, message: "Draft shift removed.", severity: "success" });
          } else {
            // For already published shifts, stage deletion to be applied on publish
            await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { pendingDelete: true, updatedAt: new Date() });
            setSnackbar({ open: true, message: "Shift marked for deletion. Publish to confirm.", severity: "info" });
          }
        } else {
          // Week not published: delete immediately
          await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { deleted: true, updatedAt: new Date() });
          setSnackbar({ open: true, message: "Shift deleted.", severity: "success" });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Error deleting shift: ${err.message || err}`, severity: "error" });
    }
    closeMenu();
  };
  const handleCopy = () => { if (menuContext.shift) setCopyBuffer({ ...menuContext.shift }); closeMenu(); };
  const handlePaste = async () => {
    if (!copyBuffer) return;
    const { emp, date, dept } = menuContext;
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot paste shift: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      closeMenu();
      return;
    }
    try {
      const targetDate = date instanceof Date ? date : new Date(date);
      const location = locations.find(loc => loc.id === selectedLocation);
      const timeZone = resolveLocationTimezone(location);
      const localDateStr = DateTime.fromJSDate(targetDate, { zone: timeZone }).toISODate();
      const tzData = computeShiftTimestamps(
        localDateStr,
        copyBuffer.shiftStartTime,
        copyBuffer.shiftEndTime,
        timeZone
      );
      const businessDateKey = getBusinessDateKey(location, tzData.startAt.toDate());
      const localWeekKey = DateTime.fromISO(businessDateKey, { zone: timeZone }).toFormat("kkkk-'W'WW");
      const roleObj = roles.find(r => r.id === copyBuffer.role);
      const rolePayrollCode = roleObj?.rolePayrollCode || null;

      const pasteData = {
        shiftDate: tzData.startAt,
        shiftStartTime: copyBuffer.shiftStartTime,
        shiftEndTime: copyBuffer.shiftEndTime,
        role: copyBuffer.role,
        roleName: roleObj?.name || roleObj?.title || "",
        rolePayrollCode,
        notes: copyBuffer.notes,
        dept,
        deleted: false,
        createdAt: new Date(),
        locationId: selectedLocation, // Add locationId for filtering
        startAt: tzData.startAt,
        endAt: tzData.endAt,
        timeZone,
        localDateKey: businessDateKey,
        localWeekKey,
        shiftDateKey: businessDateKey,
      };

      // Add employee info only if pasting to an employee (not open shifts)
      if (emp) {
        pasteData.employeeId = emp.id;
        pasteData.employeeName = emp.name;
        pasteData.isOpenShift = false;
      } else {
        // Pasting to open shifts
        pasteData.employeeId = null;
        pasteData.employeeName = null;
        pasteData.isOpenShift = true;
      }

      // If current week is published, treat pasted shifts as staged drafts
      if (isPublished) {
        pasteData.isPublished = false;
        pasteData.pendingCreate = true;
        pasteData.updatedAt = new Date();
      }

      await addDoc(collection(db, `tenants/${tenantId}/shifts`), pasteData);
      
      const message = isPublished
        ? "Shift pasted as draft. Publish to make it live."
        : (emp ? "Shift pasted." : "Shift pasted to open shifts.");
      setSnackbar({ open: true, message, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: `Error pasting shift: ${err.message || err}`, severity: "error" });
    }
    closeMenu();
  };

  // ---- DRAG AND DROP HANDLER ----
  const handleShiftMove = async (shift, target) => {
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot move shift: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      return;
    }
    try {
      if (await isShiftLockedForScheduleEdits(tenantId, shift)) {
        setSnackbar({ open: true, message: SHIFT_LOCKED_MESSAGE, severity: "warning" });
        return;
      }
      if (!target || typeof target !== 'object') {
        setSnackbar({ open: true, message: "Error: Invalid move target", severity: "error" });
        return;
      }
      if (target.dept === undefined || target.dept === null) {
        setSnackbar({ open: true, message: "Error: Department information is missing", severity: "error" });
        return;
      }
      const targetEmployee = target.employeeId ? filteredEmployees.find(e => e.id === target.employeeId) : null;
      const targetDate = new Date(target.date);
      targetDate.setHours(0, 0, 0, 0);
      const location = locations.find(loc => loc.id === selectedLocation);
      const timeZone = resolveLocationTimezone(location);
      const localDateStr = DateTime.fromJSDate(targetDate, { zone: timeZone }).toISODate();
      const tzData = computeShiftTimestamps(
        localDateStr,
        shift.shiftStartTime,
        shift.shiftEndTime,
        timeZone
      );
      const businessDateKey = getBusinessDateKey(location, tzData.startAt.toDate());
      const localWeekKey = DateTime.fromISO(businessDateKey, { zone: timeZone }).toFormat("kkkk-'W'WW");
      const roleObj = roles.find(r => r.id === target.role);
      const rolePayrollCode = roleObj?.rolePayrollCode || null;
      if (targetDate < weekStart || targetDate >= addDays(weekStart, 7)) {
        setSnackbar({ open: true, message: "Cannot move shift: target date is outside current week", severity: "error" });
        return;
      }

      if (isPublished) {
        // Stage the move as a pending update
        const stagedUpdate = {
          employeeId: target.employeeId ?? null,
          employeeName: targetEmployee?.name || (targetEmployee ? [targetEmployee.firstName, targetEmployee.lastName].filter(Boolean).join(" ") : null),
          shiftDate: tzData.startAt,
          dept: target.dept,
          role: target.role,
          roleName: roleObj?.name || roleObj?.title || "",
          rolePayrollCode,
          isOpenShift: target.isOpenShift || false,
          locationId: selectedLocation,
          startAt: tzData.startAt,
          endAt: tzData.endAt,
          timeZone,
          localDateKey: businessDateKey,
          localWeekKey,
          shiftDateKey: businessDateKey,
        };
        await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), {
          pendingUpdate: {
            ...(shift.pendingUpdate || {}),
            ...stagedUpdate
          },
          updatedAt: new Date(),
        });
        setSnackbar({ open: true, message: "Change staged. Publish to make it live.", severity: "info" });
        return;
      }

      const updates = {
        employeeId: target.employeeId,
        employeeName: targetEmployee?.name || 
          (targetEmployee ? [targetEmployee.firstName, targetEmployee.lastName].filter(Boolean).join(" ") : null),
        shiftDate: tzData.startAt,
        dept: target.dept,
        isOpenShift: target.isOpenShift || false,
        locationId: selectedLocation,
        updatedAt: new Date(),
        shiftStartTime: shift.shiftStartTime,
        shiftEndTime: shift.shiftEndTime,
  role: target.role,
        roleName: roleObj?.name || roleObj?.title || "",
        rolePayrollCode,
        notes: shift.notes || "",
        deleted: false,
        startAt: tzData.startAt,
        endAt: tzData.endAt,
        timeZone,
        localDateKey: businessDateKey,
        localWeekKey,
        shiftDateKey: businessDateKey,
      };
      if (target.isOpenShift) {
        updates.employeeId = null;
        updates.employeeName = null;
      }
      await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), updates);
      const message = target.isOpenShift ? "Shift moved to open shifts" : `Shift moved to ${updates.employeeName || 'Unknown Employee'}`;
      setSnackbar({ open: true, message, severity: "success" });
    } catch (err) {
      console.error("Error moving shift:", err);
      setSnackbar({ open: true, message: `Error moving shift: ${err.message || err}`, severity: "error" });
    }
  };

  // ---- SALES PROJECTION HANDLERS ----
  const handleSalesProjectionChange = key => e => {
    const val = e.target.value;
    // Allow empty string, numbers, and decimal numbers with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(val) || val === "") {
      setSalesProjectionsInput(prev => ({ ...prev, [key]: val }));
    }
  };

  const handleSalesProjectionBlur = key => e => {
    const val = e.target.value.trim();
    if (val === "") {
      // Clear both input and database
      setSalesProjectionsInput(prev => ({ ...prev, [key]: "" }));
      setSalesProjections(prev => ({ ...prev, [key]: 0 }));
      setDoc(doc(db, "salesProjections", key), { amount: 0 });
      return;
    }
    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal >= 0) {
      // Update both local state and database
      setSalesProjections(prev => ({ ...prev, [key]: numVal }));
      setSalesProjectionsInput(prev => ({
        ...prev,
        [key]: numVal.toFixed(2),
      }));
      setDoc(doc(db, "salesProjections", key), { amount: numVal }).catch(err => {
        console.error("Error saving sales projection:", err);
        setSnackbar({ open: true, message: `Error saving sales projection: ${err.message}` , severity: "error" });
      });
    } else {
      // Invalid input, revert to current database value
      const currentDbValue = salesProjections[key] || 0;
      setSalesProjectionsInput(prev => ({
        ...prev,
        [key]: currentDbValue ? currentDbValue.toFixed(2) : "",
      }));
    }
  };

  const handleForecastedSalesChange = key => e => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val) || val === "") {
      setForecastedSalesInput(prev => ({ ...prev, [key]: val }));
      // Keep legacy map in sync for backwards compatibility in any remaining views.
      setSalesProjectionsInput(prev => ({ ...prev, [key]: val }));
    }
  };

  const handleForecastedSalesBlur = key => async e => {
    const val = (e.target.value || "").trim();
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      setSnackbar({ open: true, message: "Unable to save forecasted sales: tenant not found", severity: "error" });
      return;
    }

    if (val === "") {
      setForecastedSalesInput(prev => ({ ...prev, [key]: "" }));
      setForecastedSales(prev => ({ ...prev, [key]: 0 }));
      setSalesProjectionsInput(prev => ({ ...prev, [key]: "" }));
      setSalesProjections(prev => ({ ...prev, [key]: 0 }));
      try {
        await setDoc(doc(db, `tenants/${tenantId}/forecastedSales`, key), { amount: 0 });
      } catch (err) {
        setSnackbar({ open: true, message: `Error saving forecasted sales: ${err.message}`, severity: "error" });
      }
      return;
    }

    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal >= 0) {
      const formatted = numVal.toFixed(2);
      setForecastedSales(prev => ({ ...prev, [key]: numVal }));
      setForecastedSalesInput(prev => ({ ...prev, [key]: formatted }));
      setSalesProjections(prev => ({ ...prev, [key]: numVal }));
      setSalesProjectionsInput(prev => ({ ...prev, [key]: formatted }));
      try {
        await setDoc(doc(db, `tenants/${tenantId}/forecastedSales`, key), { amount: numVal });
      } catch (err) {
        setSnackbar({ open: true, message: `Error saving forecasted sales: ${err.message}`, severity: "error" });
      }
    } else {
      const currentDbValue = forecastedSales[key] || salesProjections[key] || 0;
      setForecastedSalesInput(prev => ({
        ...prev,
        [key]: currentDbValue ? currentDbValue.toFixed(2) : "",
      }));
    }
  };

  const handleActualSalesChange = key => e => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val) || val === "") {
      setActualSalesInput(prev => ({ ...prev, [key]: val }));
    }
  };

  const handleActualSalesBlur = key => async e => {
    const val = (e.target.value || "").trim();
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      setSnackbar({ open: true, message: "Unable to save actual sales: tenant not found", severity: "error" });
      return;
    }

    if (val === "") {
      setActualSalesInput(prev => ({ ...prev, [key]: "" }));
      setActualSales(prev => ({ ...prev, [key]: 0 }));
      try {
        await setDoc(doc(db, `tenants/${tenantId}/actualSales`, key), { amount: 0 });
      } catch (err) {
        setSnackbar({ open: true, message: `Error saving actual sales: ${err.message}`, severity: "error" });
      }
      return;
    }

    const numVal = parseFloat(val);
    if (!isNaN(numVal) && numVal >= 0) {
      const formatted = numVal.toFixed(2);
      setActualSales(prev => ({ ...prev, [key]: numVal }));
      setActualSalesInput(prev => ({ ...prev, [key]: formatted }));
      try {
        await setDoc(doc(db, `tenants/${tenantId}/actualSales`, key), { amount: numVal });
      } catch (err) {
        setSnackbar({ open: true, message: `Error saving actual sales: ${err.message}`, severity: "error" });
      }
    } else {
      const currentDbValue = actualSales[key] || 0;
      setActualSalesInput(prev => ({
        ...prev,
        [key]: currentDbValue ? currentDbValue.toFixed(2) : "",
      }));
    }
  };

  // ----- CLEAR SCHEDULE -----
  const handleClearSchedule = async () => {
    // Resolve tenantId
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot clear schedule: tenantId not found');
      return;
    }
    
    // Mark all current week shifts as deleted for the selected location only
    const currWeekStart = getMonday(weekStart);
    const currWeekEnd = addDays(currWeekStart, 7);
    
    const q = query(
      collection(db, `tenants/${tenantId}/shifts`),
      where("shiftDate", ">=", currWeekStart),
      where("shiftDate", "<", currWeekEnd)
      // Note: Filtering by location in JavaScript to avoid composite index requirement
    );
    try {
      const snap = await getDocs(q);
      
      const batch = [];
      snap.forEach(docSnap => {
        const shiftData = docSnap.data();
        
        // Filter by locationId in JavaScript
        if (shiftData.locationId === selectedLocation) {
          batch.push(updateDoc(doc(db, `tenants/${tenantId}/shifts`, docSnap.id), { deleted: true }));
        }
      });
      
      if (batch.length > 0) {
        await Promise.all(batch);
        setSnackbar({ open: true, message: `Schedule cleared for current location (${batch.length} shifts removed).`, severity: "success" });
      } else {
        setSnackbar({ open: true, message: "No shifts found to clear for the current week and location.", severity: "info" });
      }
    } catch (err) {
      console.error('Clear schedule error:', err);
      setSnackbar({ open: true, message: `Error clearing schedule: ${err.message || err}`, severity: "error" });
    }
    setClearDialogOpen(false);
  };

  // ----- COPY SCHEDULE -----
  const handleCopySchedule = async () => {
    // Resolve tenantId
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot copy schedule: tenantId not found');
      return;
    }
    
    // Figure out source week
    let sourceStart;
    if (copyChoice === "last") sourceStart = addDays(weekStart, -7);
    else if (copyChoice === "twoweeks") sourceStart = addDays(weekStart, -14);
    else sourceStart = getMonday(customCopyDate);

    // Fetch source week shifts - use only date filter to avoid composite index requirement
    const sourceEnd = addDays(sourceStart, 7);
    const q = query(
      collection(db, `tenants/${tenantId}/shifts`),
      where("shiftDate", ">=", Timestamp.fromDate(sourceStart)),
      where("shiftDate", "<", Timestamp.fromDate(sourceEnd))
    );
    try {
      const snap = await getDocs(q);
      
      // Filter by location and deleted status in JavaScript
      const nonDeletedShifts = [];
      snap.forEach(docSnap => {
        const data = docSnap.data();
        if (!data.deleted && data.locationId === selectedLocation) {
          nonDeletedShifts.push({ id: docSnap.id, ...data });
        }
      });
      
      // Mark all current week shifts for this location as deleted
      const currWeekStart = getMonday(weekStart);
      const currWeekEnd = addDays(currWeekStart, 7);
      const currWeekQ = query(
        collection(db, `tenants/${tenantId}/shifts`),
        where("shiftDate", ">=", Timestamp.fromDate(currWeekStart)),
        where("shiftDate", "<", Timestamp.fromDate(currWeekEnd))
      );
      const currSnap = await getDocs(currWeekQ);
      
      // Filter current week shifts by location and mark them as deleted
      const deleteBatch = [];
      currSnap.forEach(docSnap => {
        const data = docSnap.data();
        if (data.locationId === selectedLocation) {
          deleteBatch.push(updateDoc(doc(db, `tenants/${tenantId}/shifts`, docSnap.id), { deleted: true }));
        }
      });
      
      await Promise.all(deleteBatch);
      
      // Copy shifts with current locationId
      const addBatch = [];
      nonDeletedShifts.forEach(shift => {
        const sourceShiftDate = shift.shiftDate.toDate ? shift.shiftDate.toDate() : new Date(shift.shiftDate);
        const dayOffset = Math.floor((sourceShiftDate - sourceStart) / (24 * 60 * 60 * 1000));
        const newShiftDate = addDays(currWeekStart, dayOffset);
        
        // Build a clean new shift object without carrying over source flags
        const {
          id: _omitId,
          pendingUpdate: _omitPendingUpdate,
          pendingDelete: _omitPendingDelete,
          pendingCreate: _omitPendingCreate,
          isPublished: _omitIsPublished,
          updatedAt: _omitUpdatedAt,
          createdAt: _omitCreatedAt,
          // Legacy attendance/payroll-derived fields must not be copied onto shifts.
          attendanceStatus: _omitAttendanceStatus,
          wasTardy: _omitWasTardy,
          leftEarly: _omitLeftEarly,
          wasAbsent: _omitWasAbsent,
          tardyMinutes: _omitTardyMinutes,
          earlyOutMinutes: _omitEarlyOutMinutes,
          workedMinutes: _omitWorkedMinutes,
          hoursMissed: _omitHoursMissed,
          evaluatedAt: _omitEvaluatedAt,
          attendanceCalcVersion: _omitAttendanceCalcVersion,
          attendanceLastWriter: _omitAttendanceLastWriter,
          ...safeCopy
        } = shift;

        const newShiftData = {
          ...safeCopy,
          shiftDate: Timestamp.fromDate(newShiftDate),
          locationId: selectedLocation, // Ensure copied shifts use current location
          deleted: false,
          // Treat copied shifts as drafts if the current week is published
          ...(isPublished ? { isPublished: false, pendingCreate: true } : {}),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        
        addBatch.push(addDoc(collection(db, `tenants/${tenantId}/shifts`), newShiftData));
      });
      
      await Promise.all(addBatch);
      
      setSnackbar({ open: true, message: `Schedule copied. ${addBatch.length} shifts added.`, severity: "success" });
    } catch (err) {
      console.error("Copy Schedule Error:", err);
      setSnackbar({ open: true, message: `Error copying schedule: ${err.message || err}`, severity: "error" });
    }
    setCopyDialogOpen(false);
  };

  // Snackbar state for user feedback
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  // Helper function to calculate adjusted break times when shift times change
  const calculateAdjustedBreakTimes = (breaks, originalStartTime, originalEndTime, newStartTime, newEndTime) => {
    if (!breaks || breaks.length === 0) return breaks;
    
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const originalStartMinutes = timeToMinutes(originalStartTime);
    const originalEndMinutes = timeToMinutes(originalEndTime);
    const originalDuration = originalEndMinutes - originalStartMinutes;
    
    const newStartMinutes = timeToMinutes(newStartTime);
    const newEndMinutes = timeToMinutes(newEndTime);
    const newDuration = newEndMinutes - newStartMinutes;
    
    return breaks.map(breakItem => {
      const breakStartMinutes = timeToMinutes(breakItem.startTime);
      
      // Calculate break's relative position within the original shift (as percentage)
      const breakRelativePosition = (breakStartMinutes - originalStartMinutes) / originalDuration;
      
      // Calculate new break start time based on the new shift timing
      const newBreakStartMinutes = newStartMinutes + (breakRelativePosition * newDuration);
      
      // Round to nearest 15 minutes for consistency
      const roundedBreakStartMinutes = Math.round(newBreakStartMinutes / 15) * 15;
      
      const newBreakStartTime = `${Math.floor(roundedBreakStartMinutes / 60).toString().padStart(2, '0')}:${(roundedBreakStartMinutes % 60).toString().padStart(2, '0')}`;
      
      return {
        ...breakItem,
        startTime: newBreakStartTime
      };
    });
  };

  // Handle shift time adjustments (for timeline view)
  const handleShiftTimeMove = async (shift, newStartTime, newEndTime) => {
    const tenantId = await tryResolveTenantId();
    if (!tenantId) {
      console.error('Cannot adjust shift time: tenantId not found');
      setSnackbar({ open: true, message: 'Error: Unable to determine tenant', severity: 'error' });
      return;
    }
    try {
      if (!shift?.id) {
        setSnackbar({ 
          open: true, 
          message: "Error: Invalid shift data", 
          severity: "error" 
        });
        return;
      }

      if (await isShiftLockedForScheduleEdits(tenantId, shift)) {
        setSnackbar({ open: true, message: SHIFT_LOCKED_MESSAGE, severity: "warning" });
        return;
      }

      // Check if times actually changed to prevent unnecessary updates
      if (shift.shiftStartTime === newStartTime && shift.shiftEndTime === newEndTime) {
        return;
      }

      // Check if there would be a duplicate shift after the update
      const wouldBeDuplicate = filteredShifts.find(s => 
        s.id !== shift.id && // Different shift
        !s.deleted &&
        s.employeeId === shift.employeeId &&
        s.dept === shift.dept &&
        s.shiftStartTime === newStartTime &&
        s.shiftEndTime === newEndTime &&
        (s.shiftDate.toDate ? s.shiftDate.toDate() : new Date(shift.shiftDate)).toISOString().split("T")[0] === 
        (shift.shiftDate.toDate ? s.shiftDate.toDate() : new Date(shift.shiftDate)).toISOString().split("T")[0]
      );
      
      if (wouldBeDuplicate) {
        setSnackbar({ 
          open: true, 
          message: "Cannot move shift: would create duplicate", 
          severity: "warning" 
        });
        return;
      }

      if (isPublished) {
        const existing = shift.pendingUpdate || {};
        const pending = { ...existing, shiftStartTime: newStartTime, shiftEndTime: newEndTime };
        await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), { pendingUpdate: pending, updatedAt: new Date() });
        setSnackbar({ open: true, message: "Change staged. Publish to make it live.", severity: "info" });
        return;
      }

      // Calculate adjusted break times
      const adjustedBreaks = calculateAdjustedBreakTimes(
        shift.breaks, 
        shift.shiftStartTime, 
        shift.shiftEndTime, 
        newStartTime, 
        newEndTime
      );

      const updateData = {
        shiftStartTime: newStartTime,
        shiftEndTime: newEndTime,
        updatedAt: new Date(),
      };
      if (adjustedBreaks && adjustedBreaks.length > 0) {
        updateData.breaks = adjustedBreaks;
      }
      await updateDoc(doc(db, `tenants/${tenantId}/shifts`, shift.id), updateData);
      setSnackbar({ open: true, message: "Shift time and breaks updated successfully", severity: "success" });
    } catch (err) {
      console.error("Error updating shift times:", err);
      setSnackbar({ open: true, message: `Error updating shift: ${err.message || err}`, severity: "error" });
    }
  };

  // Utility function to calculate schedule conflicts for an employee
  const calculateScheduleConflicts = (employeeId, weekShifts) => {
    if (!employeeId) return [];
    
    const employeeShifts = weekShifts.filter(s => 
      s.employeeId === employeeId && !s.deleted && !s.isOpenShift
    );
    
    const conflicts = [];
    
    // Check for overlapping shifts on the same day
    employeeShifts.forEach((shift1, i) => {
      employeeShifts.slice(i + 1).forEach(shift2 => {
        const date1 = shift1.shiftDate.toDate ? shift1.shiftDate.toDate() : new Date(shift1.shiftDate);
        const date2 = shift2.shiftDate.toDate ? shift2.shiftDate.toDate() : new Date(shift2.shiftDate);
        
        // Check if same day
        if (date1.toDateString() === date2.toDateString()) {
          const start1 = toMinutes(shift1.shiftStartTime);
          const end1 = toMinutes(shift1.shiftEndTime);
          const start2 = toMinutes(shift2.shiftStartTime);
          const end2 = toMinutes(shift2.shiftEndTime);
          
          // Check for overlap
          if (start1 < end2 && start2 < end1) {
            conflicts.push({
              type: 'overlap',
              date: date1,
              shift1: shift1,
              shift2: shift2,
              message: `Overlapping shifts on ${date1.toLocaleDateString()}`
            });
          }
        }
      });
    });
    
    return conflicts;
  };

  // ----------------- RENDER -----------------
  return (
    <Container maxWidth={false} disableGutters sx={{ mt: 2, mb: 4, pb: 8, overflow: 'visible' }}>
      {/* Header Section */}
      <Box ref={headerRef} sx={{ 
        mb: 4, 
        p: 3, 
        bgcolor: 'background.paper', 
        borderRadius: 2, 
        boxShadow: (theme) => theme.palette.mode === 'dark' ? 3 : 1,
        border: (theme) => theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
        overflow: 'visible',
        // Use sticky so the header remains visible when scrolling but participates in layout
        position: 'sticky',
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 10,
      }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflow: 'visible' }}>
          {/* Top Row: arrows, date, today, status, actions on right */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            rowGap: 1.5,
            overflowX: 'auto',
            overflowY: 'visible',
            pt: 0.5,
            pb: 1,
            position: 'relative',
            zIndex: 10
          }}>
            {/* Left Arrow */}
            <IconButton aria-label="Previous Week" onClick={prevWeek} size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>

            {/* Date Range display */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              minWidth: 220,
              height: 40,
              bgcolor: 'background.default'
            }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {addDays(weekStart, 6).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
            </Box>

            {/* Right Arrow (immediately next to date) */}
            <IconButton aria-label="Next Week" onClick={nextWeek} size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>

            {/* Today button (next to arrows/date) */}
            <Button variant="outlined" size="small" onClick={goToday} sx={{ height: 40, px: 2, flexShrink: 0 }}>Today</Button>

            {/* Draft/Published status directly to the right of Today */}
            {!publicationLoading && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                // Use theme-aware colors so Published isn't bright white in dark mode
                bgcolor: (theme) => isPublished
                  ? (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100])
                  : (theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.warning.light),
                color: (theme) => isPublished
                  ? (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary)
                  : (theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark),
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {isPublished ? '✓ Published' : '⚠ Draft'}
                </Typography>
                {isPublished && publicationStatus && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    by {publicationStatus.publishedBy}
                  </Typography>
                )}
                {isPublished && hasPendingChanges && (
                  <Typography variant="caption" sx={{ opacity: 0.9, ml: 1, color: 'warning.dark' }}>
                    Changes pending
                  </Typography>
                )}
              </Box>
            )}

            {/* Actions aligned to the right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <IconButton aria-label="Copy Previous" size="small" onClick={() => setCopyDialogOpen(true)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <IconButton aria-label="Tools" size="small" onClick={openToolsMenu}>
                <BuildIcon fontSize="small" />
              </IconButton>
              
              {!publicationLoading && (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!isPublished) return handlePublishSchedule();
                    if (hasPendingChanges) return handlePublishSchedule();
                    return handleUnpublishSchedule();
                  }}
                  sx={{ 
                    minWidth: 160, 
                    fontWeight: 600,
                    backgroundColor: isPublished 
                      ? (hasPendingChanges ? '#ffa726' : '#4caf50')  // Orange for pending changes, Green for published
                      : '#ffa726',  // Orange/Yellow for unpublished
                    '&:hover': {
                      backgroundColor: isPublished 
                        ? (hasPendingChanges ? '#ff9800' : '#45a049')
                        : '#ff9800'
                    }
                  }}
                >
                  {isPublished
                    ? (hasPendingChanges ? `Publish Changes (${pendingChangesCount})` : 'Unpublish')
                    : `Publish Schedule (${publishCount})`}
                </Button>
              )}
            </Box>
    </Box>

          {/* Second Row: Location & Department under date */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            rowGap: 1.5,
            overflow: 'visible',
            position: 'relative',
            zIndex: 10
          }}>
            {/* Location dropdown */}
            <FormControl size="small" sx={{ mt: 0.25, minWidth: 220, maxWidth: 280, flexShrink: 0, overflow: 'visible', position: 'relative', zIndex: 20, '& .MuiInputLabel-root': { whiteSpace: 'nowrap', lineHeight: 1.2, backgroundColor: 'background.paper', pr: 0.5, pl: 0.5, zIndex: 30 } }}>
              <InputLabel id="location-label" shrink>Location</InputLabel>
              <Select
                labelId="location-label"
                label="Location"
                native={false}
                value={selectedLocation || ''}
                onChange={(e) => setSelectedLocation(e.target.value)}
                sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
              >
                {(locations || []).map(loc => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name || loc.id}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Department dropdown (filtered by location) */}
            <FormControl size="small" sx={{ mt: 0.25, minWidth: 220, maxWidth: 280, flexShrink: 0, overflow: 'visible', position: 'relative', zIndex: 20, '& .MuiInputLabel-root': { whiteSpace: 'nowrap', lineHeight: 1.2, backgroundColor: 'background.paper', pr: 0.5, pl: 0.5, zIndex: 30 } }}>
              <InputLabel id="department-label" shrink>Department</InputLabel>
              <Select
                labelId="department-label"
                label="Department"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                displayEmpty
                renderValue={(val) => val ? (deptNameMap[val] || val) : 'All Departments'}
                sx={{ '& .MuiSelect-select': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
              >
                <MenuItem value=""><em>All Departments</em></MenuItem>
                {departmentsForLocation.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name || d.id}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Classic/Smart View Selector - aligned to the right */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                ml: 'auto',
                '& .MuiToggleButton-root': {
                  borderColor: 'primary.main',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="classic" sx={{ px: 2 }}>
                <ViewListIcon sx={{ mr: 1, fontSize: 18 }} />
                Weekly
              </ToggleButton>
              <ToggleButton value="smart" sx={{ px: 2 }}>
                <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />
                Daily
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>

        {/* Tools Menu */}
        <Menu
          anchorEl={toolsMenuAnchor}
          open={Boolean(toolsMenuAnchor)}
          onClose={closeToolsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleToolsStaffingTargets}>
            <TrackChangesIcon fontSize="small" sx={{ mr: 1 }} /> Staffing Targets
          </MenuItem>
          <MenuItem onClick={handleToolsClearSchedule}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Clear Schedule
          </MenuItem>
        </Menu>

        {/* Clear Schedule Dialog */}
        <ClearScheduleDialog
          open={clearDialogOpen}
          onClose={() => setClearDialogOpen(false)}
          onClear={handleClearSchedule}
        />

        {/* Copy Schedule Dialog */}
        <CopyScheduleDialog
          open={copyDialogOpen}
          onClose={() => setCopyDialogOpen(false)}
          copyChoice={copyChoice}
          setCopyChoice={setCopyChoice}
          customCopyDate={customCopyDate}
          setCustomCopyDate={setCustomCopyDate}
          onCopy={handleCopySchedule}
        />

        {/* Schedule Grid Section (full width) */}
        <Box sx={{ 
          mb: 4,  
          p: 0, 
          bgcolor: 'transparent', 
          borderRadius: 0, 
          boxShadow: 'none',
          border: 'none',
          width: '100%'
        }}>
          {viewMode === "classic" ? (
            <ScheduleGrid
              byDeptHierarchy={byDeptHierarchyView}
              weekDays={weekDays}
              shifts={viewShifts}
              empIdSet={empIdSet}
              analytics={analytics}
              openMenu={openMenu}
              openMenuImmediate={openMenuImmediate}
              openDialogImmediate={openDialogImmediate}
              roles={roles}
              deptNameMap={deptNameMap}
              selectedLocation={selectedLocation}
              onShiftMove={handleShiftMove}
              locations={locations}
              calculateScheduleConflicts={calculateScheduleConflicts}
              isWeekPublished={isPublished}
              headerOffset={headerOffset}
            />
          ) : (
            <ScheduleTimelineView
              byDeptHierarchy={byDeptHierarchyView}
              weekDays={weekDays}
              shifts={viewShifts}
              empIdSet={empIdSet}
              analytics={analytics}
              openMenu={openMenu}
              openMenuImmediate={openMenuImmediate}
              openDialogImmediate={openDialogImmediate}
              roles={roles}
              deptNameMap={deptNameMap}
              onShiftMove={handleShiftMove}
              onShiftTimeMove={handleShiftTimeMove}
              selectedLocation={selectedLocation}
              locations={locations}
              calculateScheduleConflicts={calculateScheduleConflicts}
              isWeekPublished={isPublished}
            />
          )}
        </Box>

        {/* Analytics Section */}
        <Box sx={{ 
          p: 3, 
          bgcolor: 'background.paper', 
          borderRadius: 2, 
          boxShadow: (theme) => theme.palette.mode === 'dark' ? 3 : 1,
          border: (theme) => theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none'
        }}>
          {/* Analytics moved to expandable Drawer. Keep this Box as an empty placeholder to preserve spacing if needed. */}
        </Box>

        {/* Analytics bottom tab (fixed) and slide-up panel */}
        <Box sx={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 12, zIndex: (theme) => theme.zIndex.drawer + 50 }}>
          <IconButton
            color="primary"
            onClick={() => setAnalyticsOpen(true)}
            sx={{ bgcolor: 'background.paper', boxShadow: 2, borderTopLeftRadius: 8, borderTopRightRadius: 8, px: 2, py: 0.5 }}
            aria-label="Open analytics"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: 16 }}>📊</span>
              <Typography variant="body2">Budget Tool</Typography>
            </Box>
          </IconButton>
        </Box>

        <Drawer
          anchor="bottom"
          open={analyticsOpen}
          onClose={() => setAnalyticsOpen(false)}
          PaperProps={{ sx: { height: { xs: '55%', sm: '38%' }, borderTopLeftRadius: 12, borderTopRightRadius: 12 } }}
        >
          <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">📊 Analytics & Projections</Typography>
              <Button onClick={() => setAnalyticsOpen(false)}>Close</Button>
            </Box>
            {/* Make the analytics content horizontally scrollable so day columns line up with the schedule */}
            <Box sx={{ overflowX: 'auto', flex: 1 }}>
              <Box sx={{ minWidth: 900 }}> {/* ensure horizontal scroll when necessary */}
                <AnalyticsTable
                  weekDays={weekDays}
                  analytics={analytics}
                  salesProjectionsInput={salesProjectionsInput}
                  handleSalesProjectionChange={handleSalesProjectionChange}
                  handleSalesProjectionBlur={handleSalesProjectionBlur}
                  forecastedSalesInput={forecastedSalesInput}
                  handleForecastedSalesChange={handleForecastedSalesChange}
                  handleForecastedSalesBlur={handleForecastedSalesBlur}
                  actualSalesInput={actualSalesInput}
                  handleActualSalesChange={handleActualSalesChange}
                  handleActualSalesBlur={handleActualSalesBlur}
                  byDept={byDept}
                  deptNameMap={deptNameMap}
                />
              </Box>
            </Box>
          </Box>
        </Drawer>

        {/* Menu & Dialog */}
        <Menu 
          open={menuPosition.mouseX !== null}
          onClose={closeMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            menuPosition.mouseY !== null && menuPosition.mouseX !== null
              ? { top: menuPosition.mouseY, left: menuPosition.mouseX }
              : undefined
          }
          disableAutoFocusItem
          disableEnforceFocus
          MenuListProps={{
            'aria-labelledby': 'basic-button',
            onKeyDown: (e) => {
              if (e.key === 'Escape') {
                closeMenu();
              }
            }
          }}
          onClick={(e) => e.stopPropagation()} // Prevent clicks inside menu from bubbling
        >
          {menuContext.shift && (
            <MenuItem onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />Copy Shift
            </MenuItem>
          )}
          {copyBuffer && (
            <MenuItem onClick={handlePaste}>
              <ContentPasteIcon fontSize="small" sx={{ mr: 1 }} />Paste Shift
            </MenuItem>
          )}
          <MenuItem onClick={openDialog}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />{menuContext.source === 'pencil' ? 'Edit Shift' : 'Add / Edit Shift'}
          </MenuItem>
          {!menuContext.emp && menuContext.source !== 'pencil' && (
            <MenuItem onClick={handleCreateOpenShift}>
              <AddIcon fontSize="small" sx={{ mr: 1 }} />Add Open Shift
            </MenuItem>
          )}
          
          <MenuItem onClick={handleDelete}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />Delete Shift
          </MenuItem>
        </Menu>

        {dialog.open && dialog.date && (
          <AddShiftDialog
            open={dialog.open}
            onClose={closeDialog}
            employee={dialog.emp}
            date={dialog.date}
            dept={dialog.dept}
            role={dialog.role}
            shift={dialog.shift}
            onSave={handleDialogSave}
            onDelete={handleDialogDelete}
            roles={roles} // Pass roles array for dropdown
            selectedLocation={selectedLocation} // Pass selectedLocation for assignments filtering
            isOpenShift={dialog.isOpenShift} // Pass open shift flag
          />
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert 
            onClose={() => setSnackbar(s => ({ ...s, open: false }))} 
            severity={snackbar.severity} 
            sx={{ 
              width: '100%',
              boxShadow: 3,
              '& .MuiAlert-message': { fontWeight: 500 }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      {/* Staffing Targets Panel */}
      <StaffingTargetsPanel
        open={staffingTargetsOpen}
        onClose={() => setStaffingTargetsOpen(false)}
        roles={rolesForSelectedLocation}
        locationId={selectedLocation}
        locationName={selectedLocationObj?.name || ''}
      />
    </Container>
  );
}
